import {NgModule} from '@angular/core';
import {PreloadAllModules, RouterModule, Routes} from '@angular/router';
import {TokenUserInfo} from './shared/services/tokenUserInfo.service';
import {FreshpassFaqComponent} from './shared/components/freshpass-faq/freshpass-faq.component';
import {ForuplusTermsComponent} from './shared/components/foruplus-terms/foruplus-terms.component';
import {ForuplusFaqComponent} from './shared/components/foruplus-faq/foruplus-faq.component';
import {B4uplusRedirectGuard} from './shared/services/b4uplus-redirect.guard';
import {ForUPlusManageResolver} from './shared/services/foruplus-manage.resolver';
import {B4UPLUS_PATH} from './shared/constants/b4uplus-routes';
import {properties} from 'src/environments/properties';

// All ForU+ (/foruplus) routes: the enrollment entry plus the already-enrolled /foruplus/manage/*
// pages. They share a single guard, B4uplusRedirectGuard, which keys off the request URL to apply
// the right redirect policy per page (enroll vs. classify-and-redirect vs. members-only) and, when
// enableB4UPlusLaunch is OFF, sends every /foruplus* URL to the legacy /freshpass/manage/subscribe
// entry so the new pages never render during pre-launch.
function generateForUPlusRoutes(): Routes {
    return [
        {
            path: B4UPLUS_PATH,
            loadChildren: () => import('./components/foruplus-registration/foruplus-registration.module').then(m => m.ForUPlusRegistrationModule),
            canActivate: [B4uplusRedirectGuard],
        },
        {
            path: `${B4UPLUS_PATH}.html`,
            loadChildren: () => import('./components/foruplus-registration/foruplus-registration.module').then(m => m.ForUPlusRegistrationModule),
            canActivate: [B4uplusRedirectGuard],
        },
        {
            path: `${B4UPLUS_PATH}/manage/plan`,
            loadChildren: () => import('./components/b4uplus-manage/b4uplus-manage.module').then(m => m.B4uplusManageModule),
            resolve: { pageData: ForUPlusManageResolver }
        },
        {
            path: `${B4UPLUS_PATH}/manage/plan.html`,
            loadChildren: () => import('./components/b4uplus-manage/b4uplus-manage.module').then(m => m.B4uplusManageModule),
            resolve: { pageData: ForUPlusManageResolver }
        },
        {
            path: `${B4UPLUS_PATH}/manage/cancel-plan`,
            loadChildren: () => import('./components/b4uplus-cancel/b4uplus-cancel.module').then(m => m.B4uplusCancelModule),
            resolve: { pageData: ForUPlusManageResolver }
        },
        {
            path: `${B4UPLUS_PATH}/manage/cancel-plan.html`,
            loadChildren: () => import('./components/b4uplus-cancel/b4uplus-cancel.module').then(m => m.B4uplusCancelModule),
            resolve: { pageData: ForUPlusManageResolver }
        },
        {
            path: `${B4UPLUS_PATH}/manage/update-payment`,
            loadChildren: () => import('./components/b4uplus-update-payment/b4uplus-update-payment.module').then(m => m.B4uplusUpdatePaymentModule),
            resolve: { pageData: ForUPlusManageResolver }
        },
        {
            path: `${B4UPLUS_PATH}/manage/update-payment.html`,
            loadChildren: () => import('./components/b4uplus-update-payment/b4uplus-update-payment.module').then(m => m.B4uplusUpdatePaymentModule),
            resolve: { pageData: ForUPlusManageResolver }
        },
        // FAQ page: publicly viewable (anonymous users included), intentionally ungated —
        // same policy as /terms and /mobile/faqs below.
        {
            path: `${B4UPLUS_PATH}/manage/foruplus-faq`,
            component: ForuplusFaqComponent,
        },
        {
            path: `${B4UPLUS_PATH}/manage/foruplus-faq.html`,
            component: ForuplusFaqComponent,
        },
        {
            path: `${B4UPLUS_PATH}/manage/discount`,
            loadChildren: () => import('./components/partners/partners.module').then(m => m.PartnersModule),
            canActivate: [B4uplusRedirectGuard]
        },
        {
            path: `${B4UPLUS_PATH}/manage/discount.html`,
            loadChildren: () => import('./components/partners/partners.module').then(m => m.PartnersModule),
            canActivate: [B4uplusRedirectGuard]
        },
        // Terms & Conditions: publicly viewable (legacy, active, and anonymous users alike), so
        // these are intentionally left ungated by B4uplusRedirectGuard — unlike the routes above,
        // a T&C page must never bounce a visitor away. /mobile/terms is the native-app webview
        // variant; server.js renders it with no AEM header/footer (mirrors govassist/mobile).
        {
            path: `${B4UPLUS_PATH}/terms`,
            component: ForuplusTermsComponent
        },
        {
            path: `${B4UPLUS_PATH}/terms.html`,
            component: ForuplusTermsComponent
        },
        {
            path: `${B4UPLUS_PATH}/mobile/terms`,
            component: ForuplusTermsComponent
        },
        {
            path: `${B4UPLUS_PATH}/mobile/terms.html`,
            component: ForuplusTermsComponent
        },
        // Standalone FAQ webview: publicly viewable and intentionally ungated (mirrors /mobile/terms).
        // /mobile/faqs is the native-app variant; server.js renders it with no AEM header/footer.
        {
            path: `${B4UPLUS_PATH}/mobile/faqs`,
            component: ForuplusFaqComponent
        },
        {
            path: `${B4UPLUS_PATH}/mobile/faqs.html`,
            component: ForuplusFaqComponent
        }
    ];
}

// Legacy enrollment route: redirect to /foruplus when the launch flag is ON, otherwise
// keep loading the original RegistrationModule (byte-for-byte today's behavior when OFF).
function generateLegacySubscribeRoutes(): Routes {
    if (properties.featureFlags.enableB4UPlusLaunch) {
        // Classify the user AT the subscribe entry and route them directly (enrolled => their
        // manage/confirmation page, not-enrolled => /foruplus), instead of bouncing everyone
        // through /foruplus first. B4uplusRedirectGuard's SUBSCRIBE branch always returns a
        // redirect UrlTree here, so the loadChildren is a never-reached fallback.
        return [
            {
                path: 'freshpass/manage/subscribe',
                canActivate: [B4uplusRedirectGuard],
                loadChildren: () => import('./components/foruplus-registration/foruplus-registration.module').then(m => m.ForUPlusRegistrationModule),
            },
            {
                path: 'freshpass/manage/subscribe.html',
                canActivate: [B4uplusRedirectGuard],
                loadChildren: () => import('./components/foruplus-registration/foruplus-registration.module').then(m => m.ForUPlusRegistrationModule),
            },
        ];
    }
    return [
        {
            path: 'freshpass/manage/subscribe',
            loadChildren: () => import('./components/registration/registration.module').then(m => m.RegistrationModule),
        },
        {
            path: 'freshpass/manage/subscribe.html',
            loadChildren: () => import('./components/registration/registration.module').then(m => m.RegistrationModule),
        },
    ];
}

// Function to generate routes
function generateRoutes(): Routes {
    return [
    // All /foruplus routes — enrollment entry + manage pages (B4uplusRedirectGuard; redirects to
    // legacy subscribe when enableB4UPlusLaunch is OFF)
    ...generateForUPlusRoutes(),
    // Main routes
    {
        path: 'freshpass/manage',
        loadChildren: () => import('./components/manage/manage-subscriptions.module').then(m => m.ManageSubscriptionsModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
        canActivate: [B4uplusRedirectGuard],
    },
    {
        path: 'freshpass/manage.html',
        loadChildren: () => import('./components/manage/manage-subscriptions.module').then(m => m.ManageSubscriptionsModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
        canActivate: [B4uplusRedirectGuard],
    },
    {
        path: 'freshpass/manage/change',
        loadChildren: () => import('./components/change-plan/change-plan.module').then(m => m.ChangePlanModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: 'freshpass/manage/change.html',
        loadChildren: () => import('./components/change-plan/change-plan.module').then(m => m.ChangePlanModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: 'freshpass/manage/confirmation',
        loadChildren: () => import('./components/confirmation/confirmation.module').then(m => m.ConfirmationModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
        canActivate: [B4uplusRedirectGuard],
    },
    {
        path: 'freshpass/manage/confirmation.html',
        loadChildren: () => import('./components/confirmation/confirmation.module').then(m => m.ConfirmationModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
        canActivate: [B4uplusRedirectGuard],
    },
    {
        path: 'freshpass/manage/cancel-plan',
        loadChildren: () => import('./components/cancel-freshpass/cancel-freshpass.module').then(m => m.CancelFreshpassModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: 'freshpass/manage/cancel-plan.html',
        loadChildren: () => import('./components/cancel-freshpass/cancel-freshpass.module').then(m => m.CancelFreshpassModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: 'freshpass/manage/update-payments',
        loadChildren: () => import('./components/update-payments/update-payments.module').then(m => m.UpdatePaymentsModule),
        resolve: {
            tokenObject: TokenUserInfo
        },
    },
    {
        path: 'freshpass/manage/update-payments.html',
        loadChildren: () => import('./components/update-payments/update-payments.module').then(m => m.UpdatePaymentsModule),
        resolve: {
            tokenObject: TokenUserInfo
        }
    },
    // Legacy enrollment route. When the ForU+ launch is ON it redirects to the new /foruplus
    // page so old bookmarks/links keep working; when OFF it renders today's enrollment unchanged.
    ...generateLegacySubscribeRoutes(),
    {
        path: 'freshpass/manage/freshpass-faq',
        component: FreshpassFaqComponent
    },
    {
        path: 'freshpass/manage/freshpass-faq.html',
        component: FreshpassFaqComponent
    },
    {
        path: 'freshpass/manage/govassist',
        loadChildren: () => import('./components/snap/snap.module').then(m => m.SnapModule),
    },
    {
        path: 'freshpass/manage/govassist.html',
        loadChildren: () => import('./components/snap/snap.module').then(m => m.SnapModule),
    },
    {
        path: 'freshpass/manage/discount',
        loadChildren: () => import('./components/partners/partners.module').then(m => m.PartnersModule),
    },
    {
        path: 'freshpass/manage/discount.html',
        loadChildren: () => import('./components/partners/partners.module').then(m => m.PartnersModule),
    }
];
}

@NgModule({
    imports: [RouterModule.forRoot([...generateRoutes()],
        {
            scrollPositionRestoration: 'enabled',
            preloadingStrategy: PreloadAllModules
        })],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
